// Pure data for the operator-curated wash palette: the `CuratedWash.Spec` type +
// the 28 promoted literals + the ordered id list. ZERO imports and no ambient
// `Option` — so standalone Deno tools (the lona-campaigns drain) can import the id
// ring directly, while the app-side `curated-washes.ts` builds rendered palettes +
// lookups on top of it.
//
// `CURATED_WASH_SPECS` is GENERATED by `lona-atoms generate.deno.ts --promote`
// (re-paste its array body here on re-curation).

export namespace CuratedWash {
  // Every stock hue family (only ~12 appear in the curated set today; the full set
  // keeps family references — e.g. the GTM nearest-family remap — type-safe).
  export type Family =
    | "aurora" | "lagoon" | "meadow" | "citrus" | "sunset" | "ember"
    | "cobalt-amber" | "orchid-lime" | "reef" | "carnival" | "festival"
    | "mist" | "dusk" | "prism" | "glacier" | "coral" | "sunset-sea"
    | "rose-pine" | "primary" | "sage";

  // The clean production param spec: the renderable ShapeSpec fields + identity.
  // Generation provenance (baseIndex / sweep / renderSize) is intentionally dropped.
  export type Spec = {
    id: string;
    name: string;
    family: Family;
    seed: number;
    colors: readonly string[];
    footprint: number;
    spread: number;
    softness: number;
    grain: number;
  };
}

// GENERATED by `lona-atoms generate.deno.ts --promote` — 28 operator-curated washes.
export const CURATED_WASH_SPECS: readonly CuratedWash.Spec[] = [
  {
    id: "meadow-1",
    name: "Meadow 1",
    family: "meadow",
    seed: 43171,
    colors: ["rgba(52, 211, 193, 0.48)", "rgba(74, 222, 165, 0.44)", "rgba(119, 230, 53, 0.4)"],
    footprint: 0.7125007532536983,
    spread: 1.1111190248280765,
    softness: 3.6932954363524915,
    grain: 0.6271390861272812,
  },
  {
    id: "meadow-2",
    name: "Meadow 2",
    family: "meadow",
    seed: 53447,
    colors: ["rgba(43, 220, 155, 0.48)", "rgba(66, 230, 126, 0.44)", "rgba(165, 240, 43, 0.4)"],
    footprint: 0.6375752496533096,
    spread: 1.12,
    softness: 2.908818819001317,
    grain: 0.72,
  },
  {
    id: "meadow-3",
    name: "Meadow 3",
    family: "meadow",
    seed: 58984,
    colors: ["rgba(53, 210, 74, 0.48)", "rgba(94, 221, 75, 0.44)", "rgba(229, 208, 54, 0.4)"],
    footprint: 0.5955693777278066,
    spread: 1.056059939544648,
    softness: 2.6,
    grain: 0.72,
  },
  {
    id: "citrus-1",
    name: "Citrus 1",
    family: "citrus",
    seed: 71527,
    colors: ["rgba(78, 226, 57, 0.4)", "rgba(179, 244, 27, 0.4)", "rgba(201, 246, 41, 0.36)"],
    footprint: 0.6184491771273315,
    spread: 0.9029523627273739,
    softness: 2.70685205347836,
    grain: 0.6275400736927986,
  },
  {
    id: "citrus-2",
    name: "Citrus 2",
    family: "citrus",
    seed: 6866,
    colors: ["rgba(68, 239, 44, 0.4)", "rgba(184, 255, 16, 0.4)", "rgba(206, 255, 32, 0.36)"],
    footprint: 0.544229489620775,
    spread: 0.86,
    softness: 2.924962529540062,
    grain: 0.6600020296126604,
  },
  {
    id: "citrus-3",
    name: "Citrus 3",
    family: "citrus",
    seed: 67881,
    colors: ["rgba(238, 214, 45, 0.4)", "rgba(255, 87, 16, 0.4)", "rgba(255, 81, 32, 0.36)"],
    footprint: 0.5,
    spread: 0.9903866897150875,
    softness: 2.6,
    grain: 0.6541777008026839,
  },
  {
    id: "citrus-4",
    name: "Citrus 4",
    family: "citrus",
    seed: 73863,
    colors: ["rgba(123, 216, 67, 0.4)", "rgba(222, 231, 40, 0.4)", "rgba(234, 228, 53, 0.36)"],
    footprint: 0.6690014775283635,
    spread: 0.86,
    softness: 2.6,
    grain: 0.5796739638224244,
  },
  {
    id: "sunset-1",
    name: "Sunset 1",
    family: "sunset",
    seed: 80484,
    colors: ["rgba(255, 193, 32, 0.48)", "rgba(255, 146, 56, 0.46)", "rgba(254, 107, 107, 0.44)"],
    footprint: 0.7285560610704124,
    spread: 1.12,
    softness: 3.8598202776163815,
    grain: 0.626942550688982,
  },
  {
    id: "sunset-2",
    name: "Sunset 2",
    family: "sunset",
    seed: 18043,
    colors: ["rgba(206, 255, 32, 0.48)", "rgba(255, 245, 56, 0.46)", "rgba(252, 180, 109, 0.44)"],
    footprint: 0.6911506939865648,
    spread: 1.0163338037393987,
    softness: 4.2,
    grain: 0.6830079186707735,
  },
  {
    id: "ember-1",
    name: "Ember 1",
    family: "ember",
    seed: 74408,
    colors: ["rgba(235, 72, 72, 0.44)", "rgba(245, 116, 116, 0.44)", "rgba(241, 117, 182, 0.4)"],
    footprint: 0.5181139032356441,
    spread: 1.0423176858015357,
    softness: 2.8303854607045653,
    grain: 0.6462491213157773,
  },
  {
    id: "ember-2",
    name: "Ember 2",
    family: "ember",
    seed: 82054,
    colors: ["rgba(239, 153, 68, 0.44)", "rgba(248, 180, 113, 0.44)", "rgba(244, 114, 117, 0.4)"],
    footprint: 0.5,
    spread: 1.0625658117979764,
    softness: 2.813865526765585,
    grain: 0.6846267922967673,
  },
  {
    id: "reef-1",
    name: "Reef 1",
    family: "reef",
    seed: 55103,
    colors: ["rgba(19, 162, 185, 0.46)", "rgba(33, 160, 239, 0.42)", "rgba(252, 147, 112, 0.44)", "rgba(252, 194, 59, 0.4)"],
    footprint: 0.7168940382078289,
    spread: 1.0506627160124482,
    softness: 3.9914369914680723,
    grain: 0.6809704093262553,
  },
  {
    id: "reef-2",
    name: "Reef 2",
    family: "reef",
    seed: 83032,
    colors: ["rgba(22, 120, 182, 0.46)", "rgba(36, 110, 236, 0.42)", "rgba(249, 182, 115, 0.44)", "rgba(249, 239, 62, 0.4)"],
    footprint: 0.6802832091599702,
    spread: 1.080140849109739,
    softness: 3.161706151068211,
    grain: 0.72,
  },
  {
    id: "reef-3",
    name: "Reef 3",
    family: "reef",
    seed: 65746,
    colors: ["rgba(16, 164, 188, 0.46)", "rgba(30, 161, 242, 0.42)", "rgba(254, 146, 110, 0.44)", "rgba(255, 195, 56, 0.4)"],
    footprint: 0.7086110089719295,
    spread: 1.0520593059249224,
    softness: 4.2,
    grain: 0.72,
  },
  {
    id: "carnival-1",
    name: "Carnival 1",
    family: "carnival",
    seed: 88289,
    colors: ["rgba(255, 106, 106, 0.44)", "rgba(43, 220, 155, 0.44)", "rgba(165, 134, 255, 0.44)"],
    footprint: 0.7614985666424037,
    spread: 1.097388475816697,
    softness: 2.6,
    grain: 0.6234614063799381,
  },
  {
    id: "carnival-2",
    name: "Carnival 2",
    family: "carnival",
    seed: 100218,
    colors: ["rgba(252, 180, 109, 0.44)", "rgba(47, 193, 216, 0.44)", "rgba(224, 136, 253, 0.44)"],
    footprint: 0.7701035956852138,
    spread: 1.0886162196472289,
    softness: 3.281958033144474,
    grain: 0.72,
  },
  {
    id: "carnival-3",
    name: "Carnival 3",
    family: "carnival",
    seed: 163595,
    colors: ["rgba(255, 106, 181, 0.44)", "rgba(41, 222, 65, 0.44)", "rgba(134, 163, 255, 0.44)"],
    footprint: 0.6428625233471394,
    spread: 1.12,
    softness: 2.6,
    grain: 0.6365042593702673,
  },
  {
    id: "carnival-4",
    name: "Carnival 4",
    family: "carnival",
    seed: 153095,
    colors: ["rgba(251, 110, 111, 0.44)", "rgba(48, 215, 154, 0.44)", "rgba(166, 137, 252, 0.44)"],
    footprint: 0.78,
    spread: 1.0434986134432258,
    softness: 3.1783343486487867,
    grain: 0.72,
  },
  {
    id: "dusk-1",
    name: "Dusk 1",
    family: "dusk",
    seed: 90159,
    colors: ["rgba(242, 172, 245, 0.3)", "rgba(235, 120, 238, 0.3)", "rgba(138, 138, 246, 0.28)"],
    footprint: 0.5,
    spread: 0.86,
    softness: 3.649149890616536,
    grain: 0.6595713461562991,
  },
  {
    id: "prism-1",
    name: "Prism 1",
    family: "prism",
    seed: 51312,
    colors: ["rgba(250, 111, 111, 0.4)", "rgba(253, 206, 18, 0.38)", "rgba(72, 224, 127, 0.38)", "rgba(53, 190, 251, 0.38)", "rgba(166, 138, 251, 0.38)"],
    footprint: 0.78,
    spread: 1.12,
    softness: 3.3315956622362135,
    grain: 0.6753992459177971,
  },
  {
    id: "prism-2",
    name: "Prism 2",
    family: "prism",
    seed: 40047,
    colors: ["rgba(247, 114, 181, 0.4)", "rgba(248, 90, 23, 0.38)", "rgba(95, 221, 75, 0.38)", "rgba(58, 246, 210, 0.38)", "rgba(140, 167, 249, 0.38)"],
    footprint: 0.78,
    spread: 1.0353393740206958,
    softness: 3.1168652292340995,
    grain: 0.72,
  },
  {
    id: "prism-3",
    name: "Prism 3",
    family: "prism",
    seed: 118647,
    colors: ["rgba(255, 180, 106, 0.4)", "rgba(183, 255, 16, 0.38)", "rgba(67, 229, 207, 0.38)", "rgba(49, 89, 255, 0.38)", "rgba(224, 134, 255, 0.38)"],
    footprint: 0.78,
    spread: 1.0426665406301618,
    softness: 4.097294826805592,
    grain: 0.72,
  },
  {
    id: "prism-4",
    name: "Prism 4",
    family: "prism",
    seed: 141994,
    colors: ["rgba(243, 149, 118, 0.4)", "rgba(230, 241, 30, 0.38)", "rgba(80, 216, 163, 0.38)", "rgba(63, 142, 241, 0.38)", "rgba(194, 144, 245, 0.38)"],
    footprint: 0.78,
    spread: 1.12,
    softness: 2.9799383129924535,
    grain: 0.72,
  },
  {
    id: "coral-1",
    name: "Coral 1",
    family: "coral",
    seed: 54625,
    colors: ["rgba(243, 164, 121, 0.48)", "rgba(241, 180, 120, 0.46)", "rgba(241, 232, 70, 0.42)"],
    footprint: 0.5442090995237232,
    spread: 1.092863711565733,
    softness: 2.6,
    grain: 0.72,
  },
  {
    id: "sunset-sea-1",
    name: "Sunset & Sea 1",
    family: "sunset-sea",
    seed: 67821,
    colors: ["rgba(255, 96, 56, 0.48)", "rgba(255, 137, 32, 0.42)", "rgba(49, 243, 255, 0.46)", "rgba(28, 150, 244, 0.42)"],
    footprint: 0.78,
    spread: 1.0288688054494561,
    softness: 2.9588122371584173,
    grain: 0.72,
  },
  {
    id: "rose-pine-1",
    name: "Rose & Pine 1",
    family: "rose-pine",
    seed: 103893,
    colors: ["rgba(250, 108, 182, 0.46)", "rgba(244, 64, 153, 0.4)", "rgba(44, 219, 155, 0.44)", "rgba(67, 229, 126, 0.4)"],
    footprint: 0.5882759931869804,
    spread: 1.0836657619290055,
    softness: 3.46886203661561,
    grain: 0.650245665460825,
  },
  {
    id: "rose-pine-2",
    name: "Rose & Pine 2",
    family: "rose-pine",
    seed: 136775,
    colors: ["rgba(245, 113, 116, 0.46)", "rgba(237, 72, 71, 0.4)", "rgba(51, 191, 212, 0.44)", "rgba(73, 223, 203, 0.4)"],
    footprint: 0.706402610335499,
    spread: 1.0270803363807501,
    softness: 3.385641537234187,
    grain: 0.7178703363239765,
  },
  {
    id: "primary-1",
    name: "Primary 1",
    family: "primary",
    seed: 58279,
    colors: ["rgba(255, 143, 106, 0.44)", "rgba(243, 255, 16, 0.42)", "rgba(50, 77, 255, 0.44)"],
    footprint: 0.697430771701038,
    spread: 1.12,
    softness: 2.6,
    grain: 0.6351183751970529,
  },
];

export const CURATED_WASH_IDS: readonly string[] = CURATED_WASH_SPECS.map((s) => s.id);
